/**
 * Board geometry.
 *
 * Kept as pure functions, away from the components that draw them, because the
 * notation is the part that has to be right: where a couple's line sits, where
 * their children hang from it, and what a zigzag looks like are all decisions a
 * clinician will read as meaning. They can be checked here without a DOM.
 *
 * Coordinates are the board's own, unitless and centred on the origin; the
 * canvas maps them to the screen.
 */

import { CENTRE_NODE_ID, type MapLink, type MapNode } from "./types";

/** Half the width of a person's shape. Everything else is derived from it. */
export const NODE_RADIUS = 22;
/**
 * How far under the lower partner the couple's line sits.
 *
 * Clear of the two lines of caption a figure carries — the name and the years
 * — because a union line drawn through someone's dates is a union line nobody
 * can read and dates nobody can read either.
 */
export const UNION_DROP = 78;
/** Spacing between the parallel lines of a close or fused relationship. */
export const PARALLEL_GAP = 4;

export interface Point {
    x: number;
    y: number;
}

export const centreOf = (node: MapNode): Point => ({ x: node.x, y: node.y });

const distance = (a: Point, b: Point) => Math.hypot(b.x - a.x, b.y - a.y);

/**
 * Where a couple's line runs, and where their children hang from it.
 *
 * Partners drop to a shared horizontal line rather than being joined corner to
 * corner, because that line is what the sibship hangs off: it is why brothers
 * and sisters read as one set of children of one couple rather than as
 * separate lines to each parent.
 */
export interface UnionGeometry {
    /** The polyline: down from each partner, across between them. */
    points: Point[];
    /** Midpoint of the horizontal run — the sibship anchor. */
    anchor: Point;
}

export const unionGeometry = (a: Point, b: Point): UnionGeometry => {
    const y = Math.max(a.y, b.y) + UNION_DROP;
    return {
        points: [
            { x: a.x, y: a.y },
            { x: a.x, y },
            { x: b.x, y },
            { x: b.x, y: b.y },
        ],
        anchor: { x: (a.x + b.x) / 2, y },
    };
};

/** The path a child link takes: straight down from the couple to the child. */
export const childPath = (anchor: Point, child: Point): Point[] => [
    anchor,
    { x: anchor.x, y: (anchor.y + child.y) / 2 },
    { x: child.x, y: (anchor.y + child.y) / 2 },
    { x: child.x, y: child.y },
];

/**
 * A note's shape on the board.
 *
 * Fixed width so a wall of notes lines up, and wrapped by hand rather than by
 * `foreignObject`: the board is rasterised into a PNG for the record, and a
 * foreign object does not survive that trip.
 */
export const NOTE_WIDTH = 190;
export const NOTE_PADDING = 12;
export const NOTE_LINE_HEIGHT = 17;
/** Roughly what fits on one line at the note's font size. */
export const NOTE_LINE_CHARS = 26;

/** Break a note into the lines it will be drawn as. */
export const wrapNote = (text: string, perLine = NOTE_LINE_CHARS): string[] => {
    const lines: string[] = [];

    for (const paragraph of text.split(/\r?\n/)) {
        let line = "";
        for (const word of paragraph.split(/\s+/).filter(Boolean)) {
            // A word longer than the note itself is cut rather than allowed to
            // run off the card — a URL, usually.
            let rest = word;
            while (rest.length > perLine) {
                if (line) {
                    lines.push(line);
                    line = "";
                }
                lines.push(rest.slice(0, perLine));
                rest = rest.slice(perLine);
            }
            if (!rest) continue;
            if (!line) line = rest;
            else if (line.length + 1 + rest.length <= perLine) line = `${line} ${rest}`;
            else {
                lines.push(line);
                line = rest;
            }
        }
        lines.push(line);
    }

    // A single empty paragraph is an empty note, which still needs a card to
    // be dragged and typed into.
    return lines.length === 0 ? [""] : lines;
};

/** How tall a note stands, given its lines and whether it names its author. */
export const noteHeight = (lines: number, hasAuthor: boolean): number =>
    NOTE_PADDING * 2 +
    Math.max(1, lines) * NOTE_LINE_HEIGHT +
    (hasAuthor ? NOTE_LINE_HEIGHT + 2 : 0);

/**
 * Where a line's own handle sits, kept clear of what it ends at.
 *
 * The midpoint is the obvious place and the wrong one when the line is short:
 * a twin hangs from an apex just above their own figure, so the middle of that
 * line falls inside the figure — which is painted after it and takes the
 * click. The handle backs off toward the start until it is outside.
 */
export const handleAlong = (from: Point, to: Point, clearance: number): Point => {
    const length = distance(from, to);
    if (length === 0) return from;

    const along = length > clearance * 2 ? length / 2 : Math.max(0, length - clearance);
    const ux = (to.x - from.x) / length;
    const uy = (to.y - from.y) / length;
    return { x: from.x + ux * along, y: from.y + uy * along };
};

/** How far under the couple's line the sibship line runs. */
export const SIBSHIP_DROP = 70;
/** How far above a twin pair their lines meet. */
export const TWIN_APEX_RISE = 34;

export interface SibshipChild {
    id: string;
    at: Point;
    /** Twins of the same kind share an apex; everyone else drops straight. */
    twin: "none" | "fraternal" | "identical";
}

export interface SibshipGeometry {
    /** Down from the couple's line to the sibship line. */
    trunk: [Point, Point];
    /** The horizontal the children hang from. */
    line: [Point, Point];
    /** One entry per child: where their own line starts and ends. */
    drops: { id: string; from: Point; to: Point }[];
    /** Pairs of identical twins, for the bar that joins them. */
    identicalBars: [Point, Point][];
}

/**
 * One sibship: a horizontal line under the couple, and a line down to each
 * child.
 *
 * This is what makes brothers and sisters read as one set of children of one
 * couple rather than as separate lines to a parent — and it is why twins can
 * be drawn at all: they meet at a point on this line instead of dropping from
 * it separately.
 */
export const sibshipGeometry = (
    anchor: Point,
    children: SibshipChild[],
): SibshipGeometry | null => {
    if (children.length === 0) return null;

    const lineY = anchor.y + SIBSHIP_DROP;
    const xs = children.map((child) => child.at.x);
    const left = Math.min(...xs, anchor.x);
    const right = Math.max(...xs, anchor.x);

    const drops: SibshipGeometry["drops"] = [];
    const identicalBars: [Point, Point][] = [];

    // Twins hang from one point rather than from their own: consecutive twins
    // of the same kind, left to right, are read as a set.
    const ordered = [...children].sort((a, b) => a.at.x - b.at.x);
    let index = 0;
    while (index < ordered.length) {
        const child = ordered[index];
        const pair =
            child.twin !== "none" &&
            ordered[index + 1]?.twin === child.twin &&
            ordered[index + 1];

        if (pair) {
            const apex = {
                x: (child.at.x + pair.at.x) / 2,
                y: lineY,
            };
            drops.push({ id: child.id, from: apex, to: child.at });
            drops.push({ id: pair.id, from: apex, to: pair.at });
            if (child.twin === "identical") {
                identicalBars.push([
                    { x: child.at.x, y: (apex.y + child.at.y) / 2 },
                    { x: pair.at.x, y: (apex.y + pair.at.y) / 2 },
                ]);
            }
            index += 2;
            continue;
        }

        drops.push({
            id: child.id,
            from: { x: child.at.x, y: lineY },
            to: child.at,
        });
        index += 1;
    }

    return {
        trunk: [anchor, { x: anchor.x, y: lineY }],
        line: [
            { x: left, y: lineY },
            { x: right, y: lineY },
        ],
        drops,
        identicalBars,
    };
};

/** Where to put the slashes that mark a separation or a divorce. */
export const unionMarkAt = (geometry: UnionGeometry): Point => geometry.anchor;

/**
 * Offsets for a relationship drawn as more than one line.
 *
 * Close is two parallel lines, fused is three: the count *is* the meaning, so
 * they are spaced evenly around the true line rather than stacked to one side,
 * which would make a fused pair look like a close pair drawn carelessly.
 */
export const parallelOffsets = (count: number): number[] => {
    const span = (count - 1) / 2;
    return Array.from({ length: count }, (_, index) => (index - span) * PARALLEL_GAP);
};

/**
 * Pull a segment's ends back, so it stops short of what it joins.
 *
 * An arrowhead drawn at a node's centre is an arrowhead under the node: the
 * shapes are painted after the lines, and a tie that ends in the middle of a
 * circle hides the very mark that says which way the energy runs.
 */
export const trimSegment = (
    a: Point,
    b: Point,
    fromGap: number,
    toGap: number,
): [Point, Point] => {
    const length = distance(a, b);
    // Two nodes on top of each other have no direction to trim along.
    if (length === 0 || fromGap + toGap >= length) return [a, b];

    const ux = (b.x - a.x) / length;
    const uy = (b.y - a.y) / length;
    return [
        { x: a.x + ux * fromGap, y: a.y + uy * fromGap },
        { x: b.x - ux * toGap, y: b.y - uy * toGap },
    ];
};

/** Shift a segment sideways by `offset`, perpendicular to its own direction. */
export const offsetSegment = (a: Point, b: Point, offset: number): [Point, Point] => {
    const length = distance(a, b) || 1;
    const nx = (-(b.y - a.y) / length) * offset;
    const ny = ((b.x - a.x) / length) * offset;
    return [
        { x: a.x + nx, y: a.y + ny },
        { x: b.x + nx, y: b.y + ny },
    ];
};

/**
 * The zigzag that means conflict.
 *
 * Built from the segment's own direction so it stays legible at any angle: a
 * hostile line drawn between two people one above the other must not collapse
 * into a straight line.
 */
export const zigzagPoints = (a: Point, b: Point, amplitude = 6, wavelength = 14): Point[] => {
    const length = distance(a, b);
    if (length === 0) return [a, b];

    const steps = Math.max(2, Math.round(length / wavelength));
    const ux = (b.x - a.x) / length;
    const uy = (b.y - a.y) / length;
    const nx = -uy;
    const ny = ux;

    const points: Point[] = [a];
    for (let step = 1; step < steps; step += 1) {
        const along = (length / steps) * step;
        const side = step % 2 === 0 ? -amplitude : amplitude;
        points.push({
            x: a.x + ux * along + nx * side,
            y: a.y + uy * along + ny * side,
        });
    }
    points.push(b);
    return points;
};

/** Two short strokes across the line: separated takes one, divorced two. */
export const slashMarks = (a: Point, b: Point, count: number, size = 9): [Point, Point][] => {
    const length = distance(a, b) || 1;
    const ux = (b.x - a.x) / length;
    const uy = (b.y - a.y) / length;
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

    return Array.from({ length: count }, (_, index) => {
        const shift = (index - (count - 1) / 2) * 8;
        const at = { x: mid.x + ux * shift, y: mid.y + uy * shift };
        // Leaning across the line, the way it is drawn by hand.
        return [
            { x: at.x - ux * size + -uy * size, y: at.y - uy * size + ux * size },
            { x: at.x + ux * size - -uy * size, y: at.y + uy * size - ux * size },
        ] as [Point, Point];
    });
};

/**
 * Two short strokes across the line, upright to it — the bars of a "quiebre".
 *
 * Unlike a cutoff's slashes these stand square to the line, which is what the
 * symbol is: the relationship stopped, not cut away at an angle.
 */
export const barMarks = (a: Point, b: Point, count: number, size = 9): [Point, Point][] => {
    const length = distance(a, b) || 1;
    const ux = (b.x - a.x) / length;
    const uy = (b.y - a.y) / length;
    const nx = -uy;
    const ny = ux;
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

    return Array.from({ length: count }, (_, index) => {
        const shift = (index - (count - 1) / 2) * 7;
        const at = { x: mid.x + ux * shift, y: mid.y + uy * shift };
        return [
            { x: at.x - nx * size, y: at.y - ny * size },
            { x: at.x + nx * size, y: at.y + ny * size },
        ] as [Point, Point];
    });
};

export const toPath = (points: Point[]): string =>
    points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`).join(" ");

/**
 * A viewport that fits everything on the board, with room around it.
 *
 * An empty board still needs a box, or the canvas divides by zero and the
 * first element lands off screen.
 */
export const fitViewBox = (
    nodes: MapNode[],
    padding = 120,
): { x: number; y: number; width: number; height: number } => {
    if (nodes.length === 0) {
        return { x: -400, y: -300, width: 800, height: 600 };
    }
    const xs = nodes.map((node) => node.x);
    const ys = nodes.map((node) => node.y);
    const minX = Math.min(...xs) - padding;
    const minY = Math.min(...ys) - padding;
    const maxX = Math.max(...xs) + padding;
    const maxY = Math.max(...ys) + padding;
    return {
        x: minX,
        y: minY,
        width: Math.max(maxX - minX, 320),
        height: Math.max(maxY - minY, 240),
    };
};

/** How close two elements may sit before the next one is moved along. */
export const MIN_SPACING = 90;

/**
 * Somewhere free to drop the next element, near where the person is looking.
 *
 * At the centre of the current view rather than at the board's origin: a
 * figure that lands off screen — or worse, behind the one you just placed —
 * is a figure you have to go and find. When the centre is taken, it spirals
 * outward until it is not.
 */
export const freeSpotNear = (
    centre: Point,
    nodes: MapNode[],
    spacing = MIN_SPACING,
): Point => {
    const taken = (at: Point) =>
        nodes.some((node) => Math.hypot(node.x - at.x, node.y - at.y) < spacing);

    if (!taken(centre)) return { x: Math.round(centre.x), y: Math.round(centre.y) };

    for (let ring = 1; ring <= 8; ring += 1) {
        const radius = ring * spacing;
        for (let step = 0; step < 8; step += 1) {
            const angle = step * (Math.PI / 4);
            const candidate = {
                x: Math.round(centre.x + Math.cos(angle) * radius),
                y: Math.round(centre.y + Math.sin(angle) * radius),
            };
            if (!taken(candidate)) return candidate;
        }
    }
    // A board that crowded is one the therapist should be spreading out
    // themselves; drop it at the centre rather than searching forever.
    return { x: Math.round(centre.x), y: Math.round(centre.y) };
};

/** Resolve the two ends of a link to points, following a child link's union. */
export const linkEndpoints = (
    link: MapLink,
    nodes: Map<string, MapNode>,
    links: Map<string, MapLink>,
): [Point, Point] | null => {
    if (link.type === "child") {
        const union = links.get(link.from);
        const child = nodes.get(link.to);
        if (!union || union.type !== "union" || !child) return null;
        const a = nodes.get(union.from);
        const b = nodes.get(union.to);
        if (!a || !b) return null;
        return [unionGeometry(centreOf(a), centreOf(b)).anchor, centreOf(child)];
    }

    const from = nodes.get(link.from === CENTRE_NODE_ID ? CENTRE_NODE_ID : link.from);
    const to = nodes.get(link.to);
    if (!from || !to) return null;
    return [centreOf(from), centreOf(to)];
};
